import React from 'react';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';

import { IS_WEB } from '@utils';
import { createAspectRatioStyles } from '@styles';
import { Touchable } from '../common/Touchable';
import { BaseText } from '../common/BaseText';
import { Separator } from '../common/Separator';
import { Image, PlaceholderTypeEnum } from '../common/Image';
import type { User } from '@models/Viewer/UserModel';

interface ITouchableContainerStyleProps {
  width?: number;
}

const TouchableContainer = styled(Touchable)(
  (props: ITouchableContainerStyleProps) => ({
    flex: 1,
    width: props.width,
    alignItems: 'center',
  }),
);

/**
 * Using AvatarImage component here is not recommended because it requires a size prop,
 * but on Vertical List we only using aspect ratio to make it responsive on different screen sizes
 */
const UserImage = styled(Image)({
  ...createAspectRatioStyles(1, '100%'),
  paddingHorizontal: '30%',
  borderRadius: 'full',
  bg: '$background20',
});

const Title = styled(BaseText)({
  color: '$heading800',
  fontWeight: 'semi',
  textAlign: 'center',
  variant: IS_WEB ? 'text.large' : 'text.medium',
});

interface IUserItemProps {
  item: User;
  width?: number;
  onPress(item: User): void;
}

export const UserItem = observer<IUserItemProps>(
  ({ item, width, onPress }) => {
    // We are using the smaller photo for loading optimization, it does not make sense to load a
    // large photo for a small Image component. It will be optimal to use the smaller photo on
    // mobile and the larger on web
    // For better optimization, it can be used useResponsiveValue hook, width prop or onLayout callback
    // instead of platform check
    const uri = IS_WEB ? item.photo.big?.url : item.photo.normal?.url;

    return (
      <TouchableContainer width={width} onPress={() => onPress(item)}>
        <UserImage
          source={{
            uri,
          }}
          placeholderType={PlaceholderTypeEnum.Avatar}
        />
        <Separator size={['$4', '$5']} />
        <Title numberOfLines={1}>{item.fullName}</Title>
      </TouchableContainer>
    );
  },
);
